/**
 * The trip dates after a tap on a search form's range calendar.
 *
 * With no dates or just a departure picked, the calendar's own range is used
 * as it comes (react-day-picker's range mode). With both dates already picked,
 * the tapped day decides:
 * - Before the departure: it becomes the new departure and the return stays.
 *   Mar 22–28, tap the 21st → Mar 21–28.
 * - On or after the departure: a new trip starts there, waiting for its
 *   return. Mar 22–28, tap the 25th → Mar 25 → ….
 *
 * This used to read the new departure from the range the calendar reported,
 * and that range isn't the tapped day. Tap before the departure, and the
 * calendar reports tapped day → old return, so the OLD RETURN became the
 * departure and the return was dropped. A customer moving Mar 22–28 to the
 * 21st got a one-way on Mar 28 (2026-10-19). A tap on either picked date
 * cleared both.
 *
 * `tappedDay` is the day react-day-picker passes as onSelect's second
 * argument. Invalid dates never reach the form.
 */
const validDate = (d) => (d instanceof Date && !Number.isNaN(d.getTime()) ? d : undefined);
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function nextSearchDateRange(current, picked, tappedDay) {
  const from = validDate(current?.from);
  const to = validDate(current?.to);
  const day = validDate(tappedDay);

  if (from && to && day) {
    if (dayStart(day) < dayStart(from)) return { from: day, to };
    return { from: day, to: undefined };
  }
  if (from && to) {
    // No tapped day reported: fall back to the end the calendar moved.
    const start = validDate(picked?.to) || validDate(picked?.from) || validDate(picked);
    return { from: start, to: undefined };
  }
  if (!picked) return { from: undefined, to: undefined };
  return {
    from: validDate(picked.from) || validDate(picked),
    to: validDate(picked.to),
  };
}

/**
 * The trip dates after a tap on Change Flight's calendar, which opens on the
 * booked dates and keeps the trip's shape: a round trip stays a round trip,
 * a one-way stays one-way.
 *
 * - Round trip: the same rule as the search form (nextSearchDateRange). Jan
 *   23–31 booked, tap the 25th → Jan 25 → …, then tap the return. The
 *   calendar's own range read used to win here, and with both dates picked
 *   it moves the RETURN for any day after the departure: the 25th gave Jan
 *   23–25, so a later departure could never be chosen (Jackson AX739M,
 *   2026-10-29, linked trips out Jan 23 / back Jan 31).
 * - One-way: the tapped day is the new departure. The calendar reports
 *   booked day → tapped day for a later day, and the booked day was kept, so
 *   a one-way couldn't move later either.
 * - A tap that reports nothing (the calendar clearing) keeps the dates.
 */
export function nextChangeFlightDateRange(current, picked, tappedDay, { roundTrip = false } = {}) {
  const day = validDate(tappedDay);
  const keep = { from: validDate(current?.from), to: roundTrip ? validDate(current?.to) : undefined };
  if (!roundTrip) {
    const from = day || validDate(picked?.from) || validDate(picked);
    return from ? { from, to: undefined } : keep;
  }
  if (!picked && !(day && validDate(current?.from) && validDate(current?.to))) return keep;
  return nextSearchDateRange(current, picked, tappedDay);
}
